import {
  IonAvatar,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  type RefresherEventDetail,
  useIonRouter,
} from "@ionic/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import EmptyAvatar from "@/assets/empty_avatar.svg";
import { Content } from "@/components/content";
import { ConversationWindowClosedIndicator } from "@/components/conversation/conversation-window-closed-indicator";
import { ConversationWindowOpenedIndicator } from "@/components/conversation/conversation-window-opened-indicator";
import { MessageBubble } from "@/components/conversation/message-bubble";
import { MessageComposer } from "@/components/conversation/message-composer";
import { useMe } from "@/lib/hooks/use-me";
import { arrowLeftIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { useSessionProvider } from "@/lib/providers/session/use-session";
import type { ConversationWindow, TypedMessage } from "@/lib/query/conversation/conversation.types";
import { useInfiniteConversations } from "@/lib/query/conversation/use-infinite-conversations";
import { useInfiniteMessages } from "@/lib/query/message/use-infinite-messages";
import { Routes } from "@/lib/router";
import { useConversationRoom, useTypingIndicator } from "@/lib/websocket/use-websocket";
import { useStatusBarHeight } from "@/utils/use-status-bar-height";

export function ConversationDetailPage() {
  const { t } = useTranslation();
  const router = useIonRouter();
  const { id } = Routes.Conversation.useParams();
  const statusBarHeight = useStatusBarHeight();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLIonContentElement>(null);
  const lastScrollTop = useRef(0);
  const [isComposerCollapsed, setIsComposerCollapsed] = useState(false);
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);
  const [composerHeight, setComposerHeight] = useState(180); // Default height estimate
  const { session } = useSessionProvider();
  const currentUserId = session.data?.user?.id;
  const currentUserDisplayName =
    session.data?.user?.firstname && session.data?.user?.lastname
      ? `${session.data.user.firstname} ${session.data.user.lastname}`
      : session.data?.user?.email || "User";
  const { isAdmin } = useMe();

  // Join WebSocket room for this conversation
  useConversationRoom(id);

  // Track typing indicators
  const { typingDisplayNames, startTyping, stopTyping } = useTypingIndicator(
    id,
    currentUserId,
    currentUserDisplayName,
  );

  // Get conversation details
  const { conversations } = useInfiniteConversations();
  const conversation = conversations.find((c) => c.id === id);

  // Get messages
  const { messages, loadMore, hasNextPage, isLoading, refetch } = useInfiniteMessages(id);

  // Use displayTitle from the conversation (computed by backend for the requesting user)
  const displayName = conversation?.displayTitle || t("routes:conversations.unknown-participant");

  // Get avatar from the first CONTACT participant
  const contactParticipant = conversation?.participants?.find((p) => p.side === "CONTACT");
  const avatarUrl =
    contactParticipant?.avatarUrl || contactParticipant?.messageContact?.avatarUrl || undefined;

  // Build participant map for determining message direction and sender info
  // Key by participant.id since message.senderId is a participant ID
  const participantMap = useMemo(() => {
    const map = new Map<
      string,
      {
        side?: "ORGANIZATION" | "CONTACT" | null;
        userId?: string;
        avatarUrl?: string;
        displayName?: string;
      }
    >();
    conversation?.participants?.forEach((p) => {
      map.set(p.id, {
        side: p.side,
        userId: p.userId,
        avatarUrl: p.avatarUrl || p.messageContact?.avatarUrl,
        displayName: p.displayName || p.messageContact?.displayName,
      });
    });
    return map;
  }, [conversation?.participants]);

  // Create a combined timeline of messages and windows sorted chronologically
  type TimelineItem =
    | { type: "message"; data: TypedMessage }
    | { type: "window"; data: ConversationWindow };

  const timelineItems = useMemo(() => {
    const items: TimelineItem[] = [];

    // Add all messages
    messages.forEach((message) => {
      items.push({ type: "message", data: message });
    });

    // Add all windows from the conversation
    conversation?.windows?.forEach((window) => {
      items.push({ type: "window", data: window });
    });

    // Sort by timestamp (openedAt for windows, createdAt for messages)
    return items.sort((a, b) => {
      const aTime =
        a.type === "message"
          ? new Date(a.data.createdAt).getTime()
          : new Date(a.data.openedAt).getTime();
      const bTime =
        b.type === "message"
          ? new Date(b.data.createdAt).getTime()
          : new Date(b.data.openedAt).getTime();
      return aTime - bTime;
    });
  }, [messages, conversation?.windows]);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages.length, scrollToBottom]);

  // Handle scroll direction detection for collapsing composer
  const handleScroll = useCallback(async () => {
    if (!contentRef.current) {
      return;
    }

    const scrollElement = await contentRef.current.getScrollElement();
    const currentScrollTop = scrollElement.scrollTop;
    const scrollDelta = currentScrollTop - lastScrollTop.current;

    // Only trigger collapse/expand after a meaningful scroll (threshold of 10px)
    if (Math.abs(scrollDelta) > 10) {
      // Scrolling up (viewing older messages) - collapse
      if (scrollDelta < 0 && currentScrollTop > 50) {
        setIsComposerCollapsed(true);
      }
      // Scrolling down (toward recent messages) - expand
      else if (scrollDelta > 0) {
        setIsComposerCollapsed(false);
      }
    }

    lastScrollTop.current = currentScrollTop;
  }, []);

  // Set up scroll listener
  useEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }

    content.addEventListener("ionScroll", handleScroll);
    return () => {
      content.removeEventListener("ionScroll", handleScroll);
    };
  }, [handleScroll]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    event.detail.complete();
  };

  const handleInfoClick = () => {
    if (id) {
      router.push(Routes.ConversationInfo({ id }));
    }
  };

  const handleBackClick = () => {
    setIsNavigatingAway(true);
    // Small delay to ensure the header disappears before transition starts
    setTimeout(() => {
      router.push(Routes.Conversations(), "back");
    }, 0);
  };

  return (
    <IonPage>
      {/* Fixed header with border */}
      {!isNavigatingAway && (
        <div
          className="fixed left-0 right-0 top-0 z-10 border-b border-bordercolor-primary bg-white"
          style={{ paddingTop: statusBarHeight }}
        >
          <div className="flex items-center justify-between px-5 pb-4 pt-4">
            {/* Left: Back button + Avatar + Name */}
            <div className="flex flex-1 items-center gap-3">
              <button
                type="button"
                onClick={handleBackClick}
                className="flex size-10 items-center justify-center text-txt-primary"
              >
                <IonIcon icon={arrowLeftIcon} className="size-5" />
              </button>

              <div className="flex items-center gap-3">
                <IonAvatar className="size-10 shrink-0">
                  <img
                    src={avatarUrl || EmptyAvatar}
                    alt={displayName}
                    className="size-full rounded-full object-cover"
                  />
                </IonAvatar>
                <span className="truncate font-semibold text-txt-primary">{displayName}</span>
              </div>
            </div>

            {/* Right: Details button */}
            {isAdmin && (
              <button
                type="button"
                onClick={handleInfoClick}
                className="border-bordercolor- rounded-xl border bg-white px-4 py-2.5 text-sm font-semibold text-txt-primary transition-opacity active:opacity-70"
              >
                {t("routes:conversations.details")}
              </button>
            )}
          </div>
        </div>
      )}

      <Content ref={contentRef} scrollEvents className="flex flex-col">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent refreshingSpinner="circles" />
        </IonRefresher>

        {/* Messages area with padding for fixed header and composer */}
        <div
          className="flex flex-1 flex-col px-5"
          style={{
            paddingTop: `calc(${statusBarHeight}px + 100px)`,
            paddingBottom: composerHeight + 16,
          }}
        >
          {/* Load more indicator at top */}
          <IonInfiniteScroll
            onIonInfinite={loadMore}
            disabled={!hasNextPage}
            position="top"
            threshold="100px"
          >
            <IonInfiniteScrollContent loadingSpinner="bubbles" />
          </IonInfiniteScroll>

          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <div className="text-txt-secondary">{t("common:loading")}</div>
            </div>
          ) : timelineItems.length === 0 ? (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-txt-secondary">{t("routes:conversations.no-messages")}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {timelineItems.map((item) => {
                if (item.type === "window") {
                  // Only show window indicators to admin users
                  if (!isAdmin) {
                    return null;
                  }
                  return item.data.isOpen ? (
                    <ConversationWindowOpenedIndicator
                      key={`window-${item.data.id}`}
                      window={item.data}
                    />
                  ) : (
                    <ConversationWindowClosedIndicator
                      key={`window-${item.data.id}`}
                      window={item.data}
                    />
                  );
                }
                return (
                  <MessageBubble
                    key={item.data.id}
                    message={item.data}
                    participantMap={participantMap}
                  />
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </Content>

      {/* Message composer - fixed at bottom */}
      {id && conversation && (
        <MessageComposer
          conversationId={id}
          conversation={conversation}
          onMessageSent={scrollToBottom}
          isCollapsed={isComposerCollapsed}
          onExpand={() => setIsComposerCollapsed(false)}
          onTypingStart={startTyping}
          onTypingStop={stopTyping}
          onHeightChange={setComposerHeight}
        />
      )}
    </IonPage>
  );
}
